import { useCallback, useEffect, useMemo, useState } from 'react';
import { BookOpen, UserCog, UserPlus, UserX } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Select } from '@/components/ui/Select';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { capitalizeWords } from '@/lib/format';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { QueryError } from '@/components/ui/QueryError';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { cn, getNameInitials } from '@/lib/utils';
import type { AllocationOptions, TeacherAllocation } from '@/types/allocation';
import { allocationsApi } from '../api/allocations.api';
import { SectionSubjectsModal } from './SectionSubjectsModal';

/**
 * Who teaches what in one class, section by section — read only.
 *
 * Every other screen answers half of this: the timetable's lesson picker shows
 * one section at a time, the staff room's allotment screen one teacher at a
 * time. Neither lets an office see that Telugu in Nursery B has nobody, or that
 * each section has a class teacher of its own rather than one for the class.
 *
 * Drawn on the classes screen and the timetable alike, so it lives beside the
 * allotments it reads. Built from the same two reads the allotment screens
 * make, so it can never disagree with them. A subject still allotted after
 * being taken off the class stays listed — hiding it would hide a teacher who
 * is still on that section's timetable.
 */

export interface ClassTeachingGridProps {
  classUuid: string;
  className: string;
  /**
   * Re-reads when this changes — the timetable passes its week, because placing
   * a lesson in an unallotted subject also allots its teacher.
   */
  reloadKey?: unknown;
  /**
   * `ALLOCATION_MANAGE` — each subject's teacher becomes a picker, saved as it
   * is changed. Without it the grid only reports who teaches what.
   */
  canEdit?: boolean;
  /**
   * Narrows the grid to one section. The classes roster draws a row per
   * section, so its panel shows only the section the reader opened.
   */
  sectionUuid?: string;
  /** Ref to imperatively trigger batch save from a parent modal footer. */
  saveRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
  /** Notifies whether uncommitted changes exist. */
  onHasChangesChange?: (hasChanges: boolean) => void;
  /** Notifies whether saving is in progress. */
  onIsSavingChange?: (isSaving: boolean) => void;
  /**
   * Opens the caller's class-teacher picker for a section — to assign one where
   * there is none, or change the one there is. When absent, the class teacher
   * is only reported, as it always was.
   */
  onAssignClassTeacher?: (sectionUuid: string) => void;
}

function cellKey(sectionUuid: string, subjectUuid: string): string {
  return `${sectionUuid}:${subjectUuid}`;
}

function buildGrid(
  options: AllocationOptions,
  allocations: TeacherAllocation[],
  classUuid: string,
  sectionUuid: string | undefined,
) {
  const thisClass = options.classes.find((one) => one.uuid === classUuid);
  const sections = (thisClass?.sections ?? []).filter(
    (section) => !sectionUuid || section.uuid === sectionUuid,
  );

  const subjectTeacher = new Map<string, { uuid: string; fullName: string }>();
  const classTeacher = new Map<string, string>();
  const subjects = new Map((thisClass?.subjects ?? []).map((one) => [one.uuid, one.name]));

  for (const allocation of allocations) {
    if (allocation.classUuid !== classUuid) continue;
    if (allocation.isClassTeacher) {
      classTeacher.set(allocation.classSectionUuid, allocation.teacher.fullName);
    }
    // A class-teacher-only allotment names the register's holder and nothing else.
    const subject = allocation.subject;
    if (!subject) continue;
    subjectTeacher.set(cellKey(allocation.classSectionUuid, subject.uuid), allocation.teacher);
    if (!subjects.has(subject.uuid)) subjects.set(subject.uuid, subject.name);
  }

  const subjectRows = [...subjects].map(([uuid, name]) => ({ uuid, name }));

  // Each section's own rows: what it teaches, and anything still allotted in it
  // even if no longer on its list — hiding that would hide a teacher who is
  // still on that section's timetable.
  const rowsBySection = new Map(
    sections.map((section) => {
      const chosen = section.subjectUuids ? new Set(section.subjectUuids) : null;
      return [
        section.uuid,
        subjectRows.filter(
          (row) => !chosen || chosen.has(row.uuid) || subjectTeacher.has(cellKey(section.uuid, row.uuid)),
        ),
      ];
    }),
  );

  let slots = 0;
  let filled = 0;
  for (const section of sections) {
    for (const row of rowsBySection.get(section.uuid) ?? []) {
      slots += 1;
      if (subjectTeacher.has(cellKey(section.uuid, row.uuid))) filled += 1;
    }
  }

  return {
    thisClass,
    sections,
    rowsBySection,
    subjectTeacher,
    classTeacher,
    slots,
    filled,
  };
}

export function ClassTeachingGrid({
  classUuid,
  className,
  reloadKey,
  canEdit = false,
  sectionUuid,
  saveRef,
  onHasChangesChange,
  onIsSavingChange,
  onAssignClassTeacher,
}: ClassTeachingGridProps) {
  /** Draft teacher uuid per cell key `${sectionUuid}:${subjectUuid}` */
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  /** The section whose subject list is open for editing. */
  const [choosingFor, setChoosingFor] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => Promise.all([allocationsApi.options(), allocationsApi.list()]),
    [reloadKey],
  );

  const grid = useMemo(
    () => (data ? buildGrid(data[0], data[1], classUuid, sectionUuid) : null),
    [data, classUuid, sectionUuid],
  );

  const teacherOptions = useMemo(() => {
    if (!data?.[0]?.teachers) return [{ value: '', label: 'Not allotted' }];
    return [
      { value: '', label: 'Not allotted' },
      ...data[0].teachers.map((one) => {
        const designation = one.designation?.trim();
        return {
          value: one.uuid,
          label: designation ? `${one.fullName} (${designation})` : one.fullName,
        };
      }),
    ];
  }, [data]);

  const pendingChanges = useMemo(() => {
    if (!grid) return [];
    const changes: {
      sectionUuid: string;
      sectionName: string;
      subjectUuid: string;
      subjectName: string;
      teacherUuid: string;
    }[] = [];

    for (const section of grid.sections) {
      const subjectRows = grid.rowsBySection.get(section.uuid) ?? [];
      for (const row of subjectRows) {
        const key = cellKey(section.uuid, row.uuid);
        if (key in draft) {
          const serverTeacherUuid = grid.subjectTeacher.get(key)?.uuid ?? '';
          if (draft[key] !== serverTeacherUuid) {
            changes.push({
              sectionUuid: section.uuid,
              sectionName: section.displayName,
              subjectUuid: row.uuid,
              subjectName: row.name,
              teacherUuid: draft[key],
            });
          }
        }
      }
    }
    return changes;
  }, [grid, draft]);

  const hasChanges = pendingChanges.length > 0;

  useEffect(() => {
    onHasChangesChange?.(hasChanges);
  }, [hasChanges, onHasChangesChange]);

  useEffect(() => {
    onIsSavingChange?.(isSaving);
  }, [isSaving, onIsSavingChange]);

  const saveAll = useCallback(async (): Promise<boolean> => {
    if (pendingChanges.length === 0) return true;
    setIsSaving(true);
    try {
      await Promise.all(
        pendingChanges.map((change) =>
          allocationsApi.setSubjectTeacher(
            change.sectionUuid,
            change.subjectUuid,
            change.teacherUuid || null,
          ),
        ),
      );
      toast.success(
        'Subject teachers saved',
        `${className} · the timetable and their console follow straight away.`,
      );
      setDraft({});
      await refetch();
      return true;
    } catch (caught) {
      toast.error(
        `Could not save subject teachers for ${className}`,
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [pendingChanges, className, refetch]);

  useEffect(() => {
    if (saveRef) {
      saveRef.current = saveAll;
    }
    return () => {
      if (saveRef) {
        saveRef.current = null;
      }
    };
  });

  const isCellAllotted = useCallback(
    (targetSectionUuid: string, targetSubjectUuid: string): boolean => {
      const key = cellKey(targetSectionUuid, targetSubjectUuid);
      if (key in draft) {
        return Boolean(draft[key]);
      }
      return grid ? grid.subjectTeacher.has(key) : false;
    },
    [draft, grid],
  );

  const draftFilled = useMemo(() => {
    if (!grid) return 0;
    let count = 0;
    for (const section of grid.sections) {
      for (const row of grid.rowsBySection.get(section.uuid) ?? []) {
        if (isCellAllotted(section.uuid, row.uuid)) {
          count += 1;
        }
      }
    }
    return count;
  }, [grid, isCellAllotted]);

  if (isLoading && !data) return <SkeletonLines count={5} />;

  if (error) {
    return <QueryError title="Could not load assigned teachers for this class" error={error} onRetry={refetch} />;
  }

  if (!grid || grid.sections.length === 0) {
    return (
      <EmptyState
        icon={<BookOpen className="h-5 w-5" />}
        title="No sections this year"
        description={`${className} has no sections in the current academic year, so nobody can be allotted to it yet.`}
      />
    );
  }

  if (grid.slots === 0) {
    return (
      <EmptyState
        icon={<BookOpen className="h-5 w-5" />}
        title="No subjects this year"
        description={`Add ${className}'s subjects on the Classes page first, then allot a teacher to each.`}
      />
    );
  }

  const percent = grid.slots > 0 ? Math.round((draftFilled / grid.slots) * 100) : 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          <span className="font-extrabold text-slate-900 dark:text-white">
            {draftFilled} of {grid.slots}
          </span>{' '}
          subject slots have a teacher
          {draftFilled < grid.slots && (
            <span className="text-slate-500 dark:text-slate-400">
              {' '}
              · {canEdit ? 'pick a teacher below' : 'fill a gap from Staff → ⋮ → Assign or edit classes'}
            </span>
          )}
        </p>
        <div className="flex items-center gap-3 shrink-0">
          <ProgressBar
            value={percent}
            label="Subject slots with a teacher"
            hideLabel
            size="sm"
            className="sm:w-44"
          />

          {!saveRef && canEdit && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => void saveAll()}
              disabled={!hasChanges || isSaving}
              isLoading={isSaving}
              className={cn(
                'shrink-0 font-bold shadow-xs transition-all',
                hasChanges && 'shadow-indigo-500/20 ring-2 ring-indigo-500/20',
              )}
            >
              Save
            </Button>
          )}
        </div>
      </div>

      <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {grid.sections.map((section) => {
          const classTeacher = grid.classTeacher.get(section.uuid);
          const subjectRows = grid.rowsBySection.get(section.uuid) ?? [];
          const allotted = subjectRows.filter((row) =>
            isCellAllotted(section.uuid, row.uuid),
          ).length;
          const isComplete = allotted === subjectRows.length;

          return (
            <li
              key={section.uuid}
              className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] shadow-xs"
            >
              <div className="flex items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#0b1322] px-4 py-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-indigo-600 text-sm font-black text-white"
                    aria-hidden
                  >
                    {section.name}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-extrabold text-slate-900 dark:text-white">
                      Section {section.name}
                    </span>
                    <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                      {section.displayName}
                    </span>
                  </span>
                </div>
                <span
                  className={cn(
                    'shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-bold tabular-nums',
                    isComplete
                      ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300'
                      : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300',
                  )}
                >
                  {allotted}/{subjectRows.length} allotted
                </span>
              </div>

              <div className="border-b border-slate-200 dark:border-slate-800 px-4 py-3">
                <p className="mb-2 text-[10.5px] font-extrabold uppercase tracking-widest text-slate-400">
                  Class teacher
                </p>
                {classTeacher ? (
                  <div className="flex items-center gap-2.5">
                    <span
                      className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/60 text-xs font-bold text-emerald-700 dark:text-emerald-300"
                      aria-hidden
                    >
                      {getNameInitials(classTeacher)}
                    </span>
                    <span className="flex-1 truncate text-sm font-bold text-slate-900 dark:text-white">
                      {classTeacher}
                    </span>
                    {onAssignClassTeacher && (
                      <button
                        type="button"
                        onClick={() => onAssignClassTeacher(section.uuid)}
                        aria-label={`Change the class teacher of ${section.displayName}`}
                        className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                      >
                        <UserCog className="h-3.5 w-3.5" aria-hidden />
                        Change
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 rounded-xl border border-dashed border-amber-300 dark:border-amber-800 px-3 py-2 text-xs font-semibold text-amber-700 dark:text-amber-300">
                    <UserX className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="flex-1">No class teacher yet</span>
                    {onAssignClassTeacher && (
                      <button
                        type="button"
                        onClick={() => onAssignClassTeacher(section.uuid)}
                        aria-label={`Assign a class teacher to ${section.displayName}`}
                        className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                      >
                        <UserPlus className="h-3.5 w-3.5" aria-hidden />
                        Assign
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="flex-1 px-4 py-3">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <p className="text-[10.5px] font-extrabold uppercase tracking-widest text-slate-400">
                    Subject teachers
                  </p>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => setChoosingFor(section.uuid)}
                      className="rounded-lg px-2 py-0.5 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    >
                      {section.subjectUuids ? 'Subjects (narrowed)' : 'Subjects'}
                    </button>
                  )}
                </div>
                <ul className="divide-y divide-slate-100 dark:divide-slate-800/60">
                  {subjectRows.map((row) => {
                    const key = cellKey(section.uuid, row.uuid);
                    const teacher = grid.subjectTeacher.get(key);
                    const serverTeacherUuid = teacher?.uuid ?? '';
                    const selectedTeacherUuid = key in draft ? draft[key] : serverTeacherUuid;

                    if (canEdit && data) {
                      return (
                        <li key={row.uuid} className="flex items-center justify-between gap-3 py-1.5">
                          <span
                            title={capitalizeWords(row.name)}
                            className="truncate text-sm font-semibold text-slate-700 dark:text-slate-200 capitalize"
                          >
                            {row.name}
                          </span>
                          <div className="w-44 shrink-0">
                            <Select
                              label={`Teacher for ${capitalizeWords(row.name)} in ${section.displayName}`}
                              hideLabel
                              options={teacherOptions}
                              value={selectedTeacherUuid}
                              disabled={isSaving}
                              minPanelWidth={280}
                              onChange={(event) => {
                                const nextTeacherUuid = event.target.value;
                                setDraft((prev) => ({
                                  ...prev,
                                  [key]: nextTeacherUuid,
                                }));
                              }}
                            />
                          </div>
                        </li>
                      );
                    }
                    return (
                      <li key={row.uuid} className="flex items-center justify-between gap-3 py-2">
                        <span className="truncate text-sm font-semibold text-slate-700 dark:text-slate-200 capitalize">
                          {row.name}
                        </span>
                        {teacher ? (
                          <span className="flex min-w-0 items-center gap-1.5">
                            <span
                              className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-indigo-50 dark:bg-indigo-950/60 text-[10px] font-bold text-indigo-600 dark:text-indigo-400"
                              aria-hidden
                            >
                              {getNameInitials(teacher.fullName)}
                            </span>
                            <span className="truncate text-sm text-slate-600 dark:text-slate-300">
                              {teacher.fullName}
                            </span>
                          </span>
                        ) : (
                          <span className="shrink-0 rounded-full border border-dashed border-amber-300 dark:border-amber-800 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300">
                            Not allotted
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </li>
          );
        })}
      </ul>

      {choosingFor && grid.thisClass && (() => {
        const section = grid.sections.find((one) => one.uuid === choosingFor);
        return section ? (
          <SectionSubjectsModal
            instituteClass={grid.thisClass}
            section={section}
            onClose={() => setChoosingFor(null)}
            onSaved={() => {
              setChoosingFor(null);
              refetch();
            }}
          />
        ) : null;
      })()}

    </div>
  );
}
